import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SecureLS from "secure-ls";
import { motion, AnimatePresence } from "framer-motion";

const ls = new SecureLS();

const getAlertKey = (alert) => {
  if (alert.name && alert.remaining !== undefined) return `low-${alert.name}`;
  if (alert.name) return `out-${alert.name}`;
  return `bill-${alert.denomination}-${alert.boxType}`;
};

const modalStyle = {
  position: "absolute",
  top: "10vh",
  left: "50%",
  transform: "translateX(-50%)",
  maxHeight: "80vh",
  width: "auto",
  maxWidth: "90vw",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const AlertsComms = ({ stockAlerts }) => {
  const [displayed, setDisplayed] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const queue = useRef([]);
  const keyMap = useRef(new Map());
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [displayed]);

  const allAlerts = [
    ...(stockAlerts.outOfStockMenus || []),
    ...(stockAlerts.lowStockMenus || []),
    ...(stockAlerts.lowStockBills || []),
  ]
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .slice(-10);

  useEffect(() => {
    const stored = ls.get("alertsSeen") || [];
    const seenKeys = new Set(stored);
    const newQueue = [];

    const currentKeys = allAlerts.map(getAlertKey);

    keyMap.current.clear();
    for (const alert of allAlerts) {
      const key = getAlertKey(alert);
      const msg = formatAlert(alert);
      keyMap.current.set(key, msg);
    }

    setDisplayed((prev) => {
      const filtered = prev.filter((line) =>
        Array.from(keyMap.current.values()).includes(line)
      );
      return filtered;
    });

    for (const alert of allAlerts) {
      const key = getAlertKey(alert);
      const msg = keyMap.current.get(key);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        newQueue.push(msg);
      }
    }

    ls.set("alertsSeen", Array.from(seenKeys));

    if (newQueue.length > 0) {
      queue.current.push(...newQueue);
    }
  }, [JSON.stringify(stockAlerts)]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (queue.current.length > 0) {
        const next = queue.current.shift();
        setDisplayed((prev) => {
          const updated = [...prev.slice(-2), next];
          return updated;
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatAlert = (alert) => {
    const time = new Date(alert.time).toLocaleTimeString("en-PH", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    let message = "";
    if (alert.name && alert.remaining !== undefined) {
      message = `🟡 Low Stock for ${alert.name} – ${alert.remaining} left`;
    } else if (alert.name) {
      message = `🔴 Out of Stock for ${alert.name}`;
    } else {
      message = `🔵 Low Bill – ₱${alert.denomination} ${alert.boxType} – ${alert.quantity}`;
    }

    return { time, message };
  };

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          maxHeight: 80,
          maxWidth: 400,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          fontSize: "0.75rem",
          color: "#444",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setModalOpen(true)}
      >
        <AnimatePresence initial={false}>
          {(displayed.length >= 3
            ? displayed.slice(-3)
            : [
                ...displayed,
                ...Array.from(keyMap.current.values()).filter(
                  (msg) =>
                    !displayed.some(
                      (d) => d.message === msg.message && d.time === msg.time
                    )
                ),
              ].slice(-3)
          ).map(({ time, message }, idx) => (
            <motion.div
              key={`${time}-${idx}`}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  color: message.includes("🔴")
                    ? "red"
                    : message.includes("🟡")
                    ? "#ff8f00"
                    : "blue",
                }}
              >
                {message} – {time}
              </Box>
            </motion.div>
          ))}
        </AnimatePresence>
      </Box>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={modalStyle}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6" fontSize="16px">
              Stock Alerts
            </Typography>
            <IconButton size="small" onClick={() => setModalOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Alert list */}
          <Box
            sx={{
              flexGrow: 1,
              mt: 1,
              overflowY: "auto",
              px: 1,
            }}
          >
            {Array.from(keyMap.current.values()).map(
              ({ time, message }, index) => (
                <Box
                  key={index}
                  onClick={() => {
                    if (message.includes("Low Bill")) {
                      window.open("/reports/bill-inventory", "_blank");
                    } else {
                      const match = message.match(/for (.+)/);
                      const menuName = match?.[1]?.split("–")[0].trim();
                      if (menuName) {
                        window.open(
                          `/menucreation?search=${encodeURIComponent(
                            menuName
                          )}`,
                          "_blank"
                        );
                      }
                    }
                  }}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    py: 0.5,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      backgroundColor: "#f1f1f1",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "90px",
                      flexShrink: 0,
                      textAlign: "right",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: message.includes("🔴")
                        ? "red"
                        : message.includes("🟡")
                        ? "#ff8f00"
                        : "blue",
                      pr: 1,
                    }}
                  >
                    {time}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "30px",
                      color: message.includes("🔴")
                        ? "red"
                        : message.includes("🟡")
                        ? "#ff8f00"
                        : "blue",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      flexGrow: 1,
                    }}
                  >
                    {message}
                  </Box>
                </Box>
              )
            )}
          </Box>

          {/* Emoji Legend */}
          <Box
            mt={2}
            sx={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              fontSize: "12px",
              color: "#444",
            }}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <span style={{ fontSize: "14px" }}>🔵</span>
              <span>Bills</span>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <span style={{ fontSize: "14px" }}>🟡</span>
              <span>Low Stock</span>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <span style={{ fontSize: "14px" }}>🔴</span>
              <span>Out of Stock</span>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AlertsComms;
